import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Language as LanguageIcon,
  People as PeopleIcon,
  Key as KeyIcon,
  Link as LinkIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Sites = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, site: null });
  const [permissionsDialog, setPermissionsDialog] = useState({ open: false, site: null, permissions: [] });
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const { isAdmin, user: currentUser } = useAuth();

  useEffect(() => {
    loadSites();
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadSites = async () => {
    try {
      setLoading(true);
      const data = await api.sites.list();
      setSites(data);
    } catch (error) {
      toast.error('Failed to load sites');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await api.users.list();
      setUsers(data);
    } catch (error) {
      // Failed to load users
    }
  };

  const handleDelete = async () => {
    try {
      await api.sites.delete(deleteDialog.site.id);
      toast.success('Site deleted successfully');
      setDeleteDialog({ open: false, site: null });
      loadSites();
    } catch (error) {
      toast.error('Failed to delete site');
    }
  };

  const handleViewPermissions = async (site) => {
    try {
      const permissions = await api.sites.getPermissions(site.id);
      setPermissionsDialog({ open: true, site, permissions });
    } catch (error) {
      toast.error('Failed to load permissions');
    }
  };

  const handleAddPermission = async (userId) => {
    try {
      await api.sites.addPermission(permissionsDialog.site.id, userId);
      toast.success('Permission added successfully');
      handleViewPermissions(permissionsDialog.site);
    } catch (error) {
      toast.error('Failed to add permission');
    }
  };

  const handleRemovePermission = async (userId) => {
    // Prevent users from removing their own permissions
    if (currentUser && userId === currentUser.id) {
      toast.error('You cannot remove your own site permissions');
      return;
    }

    try {
      await api.sites.removePermission(permissionsDialog.site.id, userId);
      toast.success('Permission removed successfully');
      handleViewPermissions(permissionsDialog.site);
    } catch (error) {
      toast.error('Failed to remove permission');
    }
  };

  const getPurgeTypeIcon = (type) => {
    switch (type) {
      case 'url':
        return <LinkIcon fontSize="small" />;
      case 'surrogate_key':
        return <KeyIcon fontSize="small" />;
      case 'purge_all':
        return <DeleteIcon fontSize="small" />;
      default:
        return <SpeedIcon fontSize="small" />;
    }
  };

  const getPurgeTypeLabel = (type) => {
    switch (type) {
      case 'url':
        return 'URL';
      case 'surrogate_key':
        return 'Surrogate Key';
      case 'purge_all':
        return 'Purge All';
      default:
        return type;
    }
  };

  const availableUsers = users.filter(
    user => user.role !== 'admin' && !permissionsDialog.permissions.some(p => p.user_id === user.id)
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={600}>
          Sites Management
        </Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/sites/new')}
          >
            Add Site
          </Button>
        )}
      </Box>

      {sites.length === 0 ? (
        <Alert severity="info">
          No sites available. {isAdmin && 'Click "Add Site" to create your first site.'}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {sites.map((site) => (
            <Grid item xs={12} md={6} lg={4} key={site.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <LanguageIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" component="h2" fontWeight={600}>
                      {site.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Domain:</strong> {site.domain}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Service ID:</strong> {site.service_id}
                  </Typography>
                  
                  {site.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Description:</strong> {site.description}
                    </Typography>
                  )}
                  
                  <Box mt={2}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Allowed Purge Types:</strong>
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {site.purge_types.map((type) => (
                        <Chip
                          key={type}
                          label={getPurgeTypeLabel(type)}
                          icon={getPurgeTypeIcon(type)}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" display="block" mt={2}>
                    Created by: {site.created_by_name || 'Unknown'}
                  </Typography>
                </CardContent>
                
                <CardActions>
                  {isAdmin && (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/sites/${site.id}/edit`)}
                        title="Edit Site"
                      >
                        <EditIcon />
                      </IconButton>
                      
                      <IconButton
                        size="small"
                        onClick={() => handleViewPermissions(site)}
                        title="Manage Permissions"
                      >
                        <PeopleIcon />
                      </IconButton>
                      
                      <IconButton
                        size="small"
                        onClick={() => setDeleteDialog({ open: true, site })}
                        title="Delete Site"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, site: null })}
      >
        <DialogTitle>Delete Site</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the site "{deleteDialog.site?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, site: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog
        open={permissionsDialog.open}
        onClose={() => setPermissionsDialog({ open: false, site: null, permissions: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Permissions - {permissionsDialog.site?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom>
            Users with Access
          </Typography>
          
          {permissionsDialog.permissions.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No users have access to this site yet.
            </Alert>
          ) : (
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <List>
                {permissionsDialog.permissions.map((permission, index) => (
                  <React.Fragment key={permission.user_id}>
                    <ListItem
                      secondaryAction={
                        permission.access_type !== 'admin' && 
                        permission.user_id !== currentUser?.id && (
                          <IconButton
                            edge="end"
                            onClick={() => handleRemovePermission(permission.user_id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )
                      }
                    >
                      <ListItemIcon>
                        <PeopleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            {permission.name}
                            {permission.access_type === 'admin' && permission.role === 'admin' && (
                              <Chip 
                                label="Admin Access" 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            )}
                            {permission.access_type === 'admin' && permission.role === 'manager' && (
                              <Chip 
                                label="Manager Access" 
                                size="small" 
                                color="secondary" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={permission.email}
                      />
                    </ListItem>
                    {index < permissionsDialog.permissions.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
          
          {availableUsers.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom>
                Add User Access
              </Typography>
              <Paper variant="outlined">
                <List>
                  {availableUsers.map((user, index) => (
                    <React.Fragment key={user.id}>
                      <ListItem
                        secondaryAction={
                          <Button
                            size="small"
                            onClick={() => handleAddPermission(user.id)}
                            variant="outlined"
                          >
                            Add
                          </Button>
                        }
                      >
                        <ListItemIcon>
                          <PeopleIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={user.name}
                          secondary={user.email}
                        />
                      </ListItem>
                      {index < availableUsers.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionsDialog({ open: false, site: null, permissions: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sites;
